import { ReactNode } from 'react';
import { TFunction } from 'i18next';
import { cn } from '../../../lib/utils';
import { tertiaryText, primaryText, semiBoldText, smallTextSize, textBlue, lightBlueBG, roundedLG } from '../../../styling/stylingHelper';
import { currencyFormatter } from '../../../services/helperService';
import { CountReviewProduct } from '../../../services/inventory/inventoryService';
import { supplierText } from './inventoryCountExport';
import { renderMobileProductAction, frozenStdUnitPrice, fmtQuantity } from './inventoryReviewShared';

/** Shared product search input — matches the Needs attention section styling. */
export function InventoryProductSearchInput({
  value,
  onChange,
  placeholder,
  className,
}: {
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
  className?: string;
}) {
  return (
    <div className={cn('relative w-full md:w-[202px] flex-shrink-0', className)}>
      <svg
        className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#667085] dark:text-textTertiary-dark pointer-events-none"
        viewBox="0 0 20 20"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M17.5 17.5L14.5834 14.5833M16.6667 9.58333C16.6667 13.4954 13.4954 16.6667 9.58333 16.6667C5.67132 16.6667 2.5 13.4954 2.5 9.58333C2.5 5.67132 5.67132 2.5 9.58333 2.5C13.4954 2.5 16.6667 5.67132 16.6667 9.58333Z"
          stroke="currentColor"
          strokeWidth="1.66667"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full pl-10 pr-3 h-11 rounded-xl border border-[#D0D5DD] dark:border-[#2D4F68] bg-white dark:bg-[#14344C] text-[16px] leading-6 font-normal text-[#111827] dark:text-textPrimary-dark placeholder:text-[#667085] dark:placeholder:text-textTertiary-dark outline-none"
      />
    </div>
  );
}

function MobileInfoColumn({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="flex flex-col gap-0.5 px-4 first:pl-0 last:pr-0">
      <span className={cn(tertiaryText, 'text-[12px] leading-[18px] font-medium truncate')}>{label}</span>
      <span className={cn(primaryText, smallTextSize, semiBoldText, 'truncate leading-5')}>{value}</span>
    </div>
  );
}

export interface InventoryProductMobileCardProps {
  row: CountReviewProduct;
  t: TFunction;
  categoryLabel: string;
  categoryValue: ReactNode;
  actionHandlers?: {
    onRelink?: (row: CountReviewProduct) => void;
    onEditCustom?: (row: CountReviewProduct) => void;
    onSetPrice?: (row: CountReviewProduct) => void;
    onSetup?: (row: CountReviewProduct) => void;
  };
}

/** Shared mobile product card used by the attention block and section detail page. */
export function InventoryProductMobileCard({
  row,
  t,
  categoryLabel,
  categoryValue,
  actionHandlers,
}: InventoryProductMobileCardProps) {
  const unitPrice = frozenStdUnitPrice(row);
  const action = actionHandlers ? renderMobileProductAction(t, row, actionHandlers) : null;

  return (
    <div className="rounded-xl bg-white dark:bg-[#0E2B42] p-4">
      <div className="flex items-center justify-between gap-3 mb-3">
        <span className={cn(primaryText, semiBoldText, 'text-[14px] leading-5 min-w-0')}>{row.productName}</span>
        <span
          className={cn(
            textBlue,
            semiBoldText,
            lightBlueBG,
            roundedLG,
            'text-[14px] dark:bg-[#2E90FA26] dark:text-[#2E90FA] px-2 py-1 whitespace-nowrap shrink-0'
          )}
        >
          {`${fmtQuantity(row.quantityDisplay.value)} ${row.quantityDisplay.unit}`}
        </span>
      </div>

      <div className="flex border-y border-[#EAECF0] dark:border-borderPrimary-dark py-3">
        <MobileInfoColumn label={t('inventory.colSupplier')} value={supplierText(row.supplier)} />
        <div className="w-px bg-[#EAECF0] dark:bg-borderPrimary-dark shrink-0" />
        <MobileInfoColumn label={categoryLabel} value={categoryValue} />
        <div className="w-px bg-[#EAECF0] dark:bg-borderPrimary-dark shrink-0" />
        <MobileInfoColumn
          label={t('inventory.unitPrice')}
          value={unitPrice ? `${currencyFormatter.format(unitPrice.value)} / ${unitPrice.unit}` : '—'}
        />
      </div>

      <div className="flex items-center justify-between pt-3">
        <div className="min-w-0">
          <span className={cn(tertiaryText, 'text-[14px] leading-[18px] font-normal')}>{t('inventory.value')} </span>
          <span className={cn(primaryText, 'text-[14px] font-medium leading-5 ml-1')}>{currencyFormatter.format(row.value)}</span>
        </div>
        {action ?? <span className="text-[14px] text-[#667085] dark:text-textTertiary-dark">—</span>}
      </div>
    </div>
  );
}
